import React, { useState, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import type { CheckfieldProps } from "./Checkfield.types";
import "./checkfield.scss";

const INPUT_STATES = {
  Checked: "Checked",
  Indeterminate: "Indeterminate",
  Empty: "Empty",
};

const Checkfield = React.forwardRef<any, CheckfieldProps>(({
  text,
  value,
  type,
  checked,
  color,
  size,
  disabled,
  error,
  invert,
  onChange,
  name,  
}, ref) => {
  const inputRef = useRef<any>(ref);
  const [isChecked, setIsChecked] = useState(checked);

  const handleChange = (e: any) => {
    let updatedChecked: "Empty" | "Checked" | "Indeterminate";

    if (isChecked === INPUT_STATES.Checked) {
      updatedChecked = "Empty";
    } else {
      updatedChecked = "Checked";
    }

    setIsChecked(updatedChecked);
    onChange && onChange(e);
  };

  const isDisabled = disabled ? "sb-checkfield--disabled" : "";
  const hasError = error ? "sb-checkfield--error" : "";
  const isInvert =
    !error && type === "radio" && invert ? "sb-checkfield--invert" : "";

  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.checked = checked === INPUT_STATES.Checked;
      inputRef.current.indeterminate = checked === INPUT_STATES.Indeterminate;
    }
  }, [inputRef, checked]);

  return (
    <div
      className={[
        "sb-checkfield",
        `sb-checkfield--${color}`,
        `sb-checkfield--${type}`,
        `sb-checkfield--${size}`,
        isDisabled,
        isInvert,
        hasError,
      ].join(" ")}
      data-testid="sb-checkfield"
    >
      <label data-testid="sb-checkfield__label">
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          data-testid="sb-checkfield__input"
          name={name}
        />
        {text}
      </label>
    </div>
  );
});

Checkfield.propTypes = {
  /**
   * Checkfield text
   */
  text: PropTypes.string.isRequired,
  /**
   * Checkfield value
   */
  value: PropTypes.string,
  /**
   * Color of checkfield
   */
  color: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  /**
   * type of checkfield
   */
  type: PropTypes.oneOf(["radio", "checkbox"]),
  /**
   * size
   */
  size: PropTypes.oneOf(["sm", "md"]),
  /**
   * checked
   */
  checked: PropTypes.oneOf(["Empty", "Checked", "Indeterminate"]),
  /**
   * Disabled
   */
  disabled: PropTypes.bool,
  /**
   * error
   */
  error: PropTypes.bool,
  /**
   * invert
   */
  invert: PropTypes.bool,
  /**
   * onChange
   */
  onChange: PropTypes.func,
  /**
   * name
   */
  name: PropTypes.string,
  /**
   * ref
   */
  ref: PropTypes.any,
};

Checkfield.defaultProps = {
  text: "Text",
  color: "primary",
  value: "",
  type: "radio",
  checked: "Empty",
  size: "sm",
  disabled: false,
  error: false,
  invert: false,
  name: "",
  ref: null,
  onChange: () => {},
};

export default Checkfield;
